import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft } from "lucide-react";
import { useWorkflowDefinition } from "@/hooks/useWorkflowDefinitions";
import { getModuleDef, MODULE_REGISTRY } from "@/workflows/moduleRegistry";
import { WorkflowRunnerProvider } from "@/workflows/runnerContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import { toast } from "sonner";

const COMPLETE_KEY = "workflow_complete";

export default function WorkflowRunner() {
  const { id: applianceId, workflowKey } = useParams<{ id: string; workflowKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, isLoading } = useWorkflowDefinition(workflowKey);

  const stepParam = Number(searchParams.get("step") ?? "0");
  const [step, setStep] = useState(Number.isFinite(stepParam) ? stepParam : 0);
  const [workOrderId, setWorkOrderId] = useState<string | null>(searchParams.get("wo"));
  const [moduleState, setModuleState] = useState<Record<string, unknown>>({});
  const [status, setStatus] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [resolvedApplianceId, setResolvedApplianceId] = useState<string | null>(null);
  const completedWriteRef = useRef(false);
  const effectiveApplianceId = resolvedApplianceId || applianceId;

  const modules = data?.modules ?? [];
  const completeIndex = useMemo(
    () => modules.findIndex((m) => m.module_key === COMPLETE_KEY),
    [modules],
  );
  const isCompleted = status === "completed";

  // Sync step ↔ URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("step", String(step));
    if (workOrderId) {
      next.set("wo", workOrderId);
      next.set("workOrderId", workOrderId);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, workOrderId]);

  // Resolve / create work order, load persisted module state + step + status
  useEffect(() => {
    if (!applianceId || !workflowKey || !data) return;
    setIsHydrated(false);
    (async () => {
      try {
        const companyId = await fetchCurrentCompanyId();
        if (!companyId) return;
        let woId = workOrderId;
        const applyLoaded = (row: any) => {
          const woData = (row?.data ?? {}) as any;
          setModuleState((woData.modules ?? {}) as Record<string, unknown>);
          setStatus((row?.status ?? null) as string | null);
          if (row?.appliance_id) setResolvedApplianceId(row.appliance_id as string);
          if (typeof woData.currentStep === "number" && !searchParams.get("step")) {
            setStep(woData.currentStep);
          }
        };
        if (!woId) {
          const { data: existing } = await (supabase as any)
            .from("work_orders")
            .select("id, data, status, appliance_id")
            .eq("appliance_id", applianceId)
            .eq("type", workflowKey)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (existing) {
            woId = existing.id;
            applyLoaded(existing);
          } else {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: created, error } = await (supabase as any)
              .from("work_orders")
              .insert({
                appliance_id: applianceId,
                type: workflowKey,
                user_id: user.id,
                company_id: companyId,
                data: { modules: {}, currentStep: 0 },
              })
              .select("id, status, appliance_id")
              .single();
            if (error) throw error;
            woId = created.id;
            setResolvedApplianceId(created.appliance_id ?? applianceId ?? null);
            setStatus(created.status ?? "in_progress");
          }
          setWorkOrderId(woId);
        } else {
          const { data: existing } = await (supabase as any)
            .from("work_orders")
            .select("data, status, appliance_id")
            .eq("id", woId)
            .maybeSingle();
          if (existing) applyLoaded(existing);
        }
      } catch (err) {
        console.error("[WorkflowRunner] init failed", err);
      } finally {
        setIsHydrated(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applianceId, workflowKey, data?.definition?.id]);

  // For the harvesting workflow, the rich "Completed" summary lives inside
  // HarvestProcess (rendered by the harvest_components_picker module). Skip the
  // generic workflow_complete screen by redirecting back to that module.
  const harvestPickerIndex = useMemo(
    () => modules.findIndex((m) => m.module_key === "harvest_components_picker"),
    [modules],
  );
  const isHarvesting = workflowKey === "harvesting";

  // When the work order is already completed, force the runner onto the
  // complete stage (or past the end if no complete module is configured).
  useEffect(() => {
    if (!isHydrated || !isCompleted || modules.length === 0) return;
    let target = completeIndex >= 0 ? completeIndex : modules.length;
    if (isHarvesting && harvestPickerIndex >= 0) target = harvestPickerIndex;
    if (step !== target) setStep(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, isCompleted, completeIndex, modules.length, isHarvesting, harvestPickerIndex]);

  // Harvesting: if forward navigation lands on workflow_complete, bounce back
  // to the harvest picker module so HarvestProcess step 5 is the sole summary.
  useEffect(() => {
    if (!isHydrated || !isHarvesting || harvestPickerIndex < 0) return;
    const cur = modules[step];
    if (cur?.module_key === COMPLETE_KEY) setStep(harvestPickerIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated, isHarvesting, harvestPickerIndex, step, modules.length]);


  const current = modules[step];
  const moduleDef = current ? getModuleDef(current.module_key) : undefined;

  const persist = async (moduleKey: string, patch: unknown) => {
    if (!workOrderId || isCompleted) return;
    const next = { ...moduleState, [moduleKey]: { ...(moduleState[moduleKey] as object ?? {}), ...(patch as object ?? {}) } };
    setModuleState(next);
    const { error } = await (supabase as any)
      .from("work_orders")
      .update({ data: { modules: next, currentStep: step } })
      .eq("id", workOrderId);
    if (error) toast.error("Failed to save progress");
  };

  // Persist current step whenever it changes (skip while completed).
  useEffect(() => {
    if (!workOrderId || !isHydrated || isCompleted) return;
    (supabase as any)
      .from("work_orders")
      .update({ data: { modules: moduleState, currentStep: step } })
      .eq("id", workOrderId)
      .then(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, workOrderId, isHydrated, isCompleted]);

  // Auto-mark completed when the runner reaches the workflow_complete module
  // via normal forward navigation.
  useEffect(() => {
    if (!workOrderId || !isHydrated || isCompleted) return;
    if (!current || current.module_key !== COMPLETE_KEY) return;
    if (completedWriteRef.current) return;
    completedWriteRef.current = true;
    (async () => {
      const completedAt = new Date().toISOString();
      const { error } = await (supabase as any)
        .from("work_orders")
        .update({ status: "completed", completed_at: completedAt })
        .eq("id", workOrderId);
      if (error) {
        completedWriteRef.current = false;
        console.error("[WorkflowRunner] failed to mark completed", error);
      } else {
        setStatus("completed");
      }
    })();
  }, [workOrderId, isHydrated, isCompleted, current?.module_key]);

  if (isLoading || !isHydrated) return <div className="p-6"><Skeleton className="h-40 w-full" /></div>;
  if (!data) return <div className="p-6"><Card className="p-6">Workflow not found.</Card></div>;
  if (data.disabled) {
    return (
      <div className="p-6">
        <Card className="p-6 space-y-3">
          <h2 className="text-xl font-semibold">Workflow unavailable</h2>
          <p className="text-sm text-muted-foreground">
            The “{data.definition.name}” workflow is disabled for your company. Ask an admin to enable it in
            Admin → Workflow Definitions.
          </p>
          <Button onClick={() => navigate(`/appliance/${effectiveApplianceId}`)}>Back to appliance</Button>
        </Card>
      </div>
    );
  }

  // Fallback complete card if a workflow has no workflow_complete module
  // configured: render the reusable module with default config so behaviour
  // is identical everywhere.
  if (!current) {
    const FallbackComplete = MODULE_REGISTRY[COMPLETE_KEY].component as any;
    const fallbackConfig = MODULE_REGISTRY[COMPLETE_KEY].defaultConfig as any;
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/appliance/${effectiveApplianceId}`)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{data.definition.name} complete</h1>
          </div>
        </div>
        <div className="px-6">
          <FallbackComplete
            workOrderId={workOrderId}
            applianceId={effectiveApplianceId!}
            workflowKey={workflowKey!}
            config={fallbackConfig}
            state={undefined}
            onComplete={() => undefined}
            onBack={() => undefined}
          />
        </div>
      </div>
    );
  }

  if (!moduleDef) {
    return (
      <div className="p-6 space-y-3">
        <Card className="p-6 space-y-3">
          <p>Unknown module: <code>{current.module_key}</code></p>
          <Button onClick={() => setStep((s) => s + 1)}>Skip</Button>
        </Card>
      </div>
    );
  }

  const Component = moduleDef.component;
  const config = { ...(moduleDef.defaultConfig as object), ...((current.config as object) ?? {}) };
  const lockNav = isCompleted || current.module_key === COMPLETE_KEY;

  const hideRunnerHeader = workflowKey === "harvesting";

  return (
    <div className="min-h-screen bg-background pb-24">
      {!hideRunnerHeader && (
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/appliance/${effectiveApplianceId}`)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{current.title_override || moduleDef.label}</h1>
            <p className="text-xs text-muted-foreground">
              {data.definition.name} • Step {step + 1} of {modules.length}
            </p>
          </div>
        </div>
      )}
      <div className={hideRunnerHeader ? "" : "px-6"}>
        <WorkflowRunnerProvider
          value={{
            workOrderId,
            applianceId: effectiveApplianceId!,
            workflowKey: workflowKey!,
            state: moduleState[current.module_key],
            persist: async (patch) => {
              if (lockNav) return;
              await persist(current.module_key, patch);
            },
            complete: async (patch) => {
              if (lockNav) return;
              if (patch !== undefined) await persist(current.module_key, patch);
              setStep((s) => Math.min(s + 1, modules.length));
            },
            markCompleted: async (patch) => {
              if (!workOrderId) return;
              const nextState = patch !== undefined
                ? { ...moduleState, [current.module_key]: { ...((moduleState[current.module_key] as object) ?? {}), ...((patch as object) ?? {}) } }
                : moduleState;
              if (patch !== undefined) setModuleState(nextState);
              const target = completeIndex >= 0 ? completeIndex : modules.length;
              const completedAt = new Date().toISOString();
              completedWriteRef.current = true;
              const { error } = await (supabase as any)
                .from("work_orders")
                .update({
                  status: "completed",
                  completed_at: completedAt,
                  data: { modules: nextState, currentStep: target },
                })
                .eq("id", workOrderId);
              if (error) {
                completedWriteRef.current = false;
                toast.error("Failed to complete work order");
                return;
              }
              setStatus("completed");
              setStep(target);
            },
            back: () => {
              if (lockNav) return;
              setStep((s) => Math.max(0, s - 1));
            },
            skip: () => {
              if (lockNav) return;
              setStep((s) => s + 1);
            },
          }}
        >
          <Component
            workOrderId={workOrderId}
            applianceId={effectiveApplianceId!}
            workflowKey={workflowKey!}
            config={config}
            state={moduleState[current.module_key]}
            onComplete={async (patch) => {
              if (lockNav) return;
              if (patch !== undefined) await persist(current.module_key, patch);
              setStep((s) => Math.min(s + 1, modules.length));
            }}
            onBack={() => {
              if (lockNav) return;
              setStep((s) => Math.max(0, s - 1));
            }}
            onSkip={() => {
              if (lockNav) return;
              setStep((s) => s + 1);
            }}
          />
        </WorkflowRunnerProvider>
      </div>
    </div>
  );
}
